/**
 * An HTML document of `.page` sections, as a real PDF (owner, 2026-09-27: "better send PDFs").
 *
 * Each page is drawn by the browser itself - so Bengali, Devanagari, Kannada and the rest are
 * shaped exactly as they are on screen, which a PDF library's own text engine does not do for
 * Indic scripts - then placed on an A4 page. The libraries load only when a PDF is made.
 */
export async function htmlToPdf(html: string, fileName: string): Promise<File> {
  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import('jspdf'), import('html2canvas-pro')]);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:820px;height:1200px;border:0;';
  document.body.appendChild(frame);
  try {
    const doc = frame.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();
    await new Promise((resolve) => { if (doc.readyState === 'complete') resolve(null); else frame.onload = () => resolve(null); });
    await doc.fonts?.ready;
    await Promise.all([...doc.images].map((image) => (image.complete ? null : new Promise((resolve) => { image.onload = image.onerror = resolve; }))));

    paginate(doc);
    const pages = [...doc.querySelectorAll<HTMLElement>('.page')];
    if (pages.length === 0) throw new Error('The document has no pages.');
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
    for (const [index, page] of pages.entries()) {
      const canvas = await html2canvas(page, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
      if (index > 0) pdf.addPage();
      const width = 210;
      const height = Math.min(297, (canvas.height / canvas.width) * width);
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.86), 'JPEG', 0, 0, width, height);
    }
    const blob = pdf.output('blob');
    return new File([blob], fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`, { type: 'application/pdf' });
  } finally {
    frame.remove();
  }
}

const FIXED = ':scope > .foot, :scope > .block, :scope > .wm';
const isFixed = (element: Element) => element.matches('.foot, .block, .wm');
const flowOf = (element: HTMLElement) => [...element.children].filter((child) => !isFixed(child)) as HTMLElement[];
/** Where a container's movable children live: a table's rows, anything else's own children. */
const itemsOf = (element: HTMLElement): HTMLElement => (element.tagName === 'TABLE' ? ((element as HTMLTableElement).tBodies[0] ?? element) : element);
const splittable = (element: HTMLElement) => /^(UL|OL|TABLE|DIV)$/.test(element.tagName) && itemsOf(element).children.length > 1
  && !element.matches('.person, .sign, .agree, .given, .geo, .block, .foot, .wm');

/**
 * A page longer than A4 is continued on a new sheet (owner, 2026-09-28: the two-language documents run
 * longer): the sheet keeps its footer, watermark and, on a print copy, its signature block; what does
 * not fit moves on - whole paragraphs, or a list, table or section split between its items. Then
 * every sheet is numbered "3/12" where the document left a `.pno` for it.
 */
export function paginate(doc: Document) {
  const fits = (page: HTMLElement) => page.scrollHeight <= page.clientHeight + 1;
  let page = doc.querySelector<HTMLElement>('.page');
  let guard = 0;
  while (page && guard < 500) {
    guard += 1;
    if (!fits(page)) {
      const next = page.cloneNode(false) as HTMLElement;
      page.querySelectorAll(FIXED).forEach((fixed) => next.appendChild(fixed.cloneNode(true)));
      page.after(next);
      const anchor = () => flowOf(next)[0] ?? next.querySelector(FIXED);
      const place = (element: HTMLElement, into: HTMLElement, before: Element | null) => into.insertBefore(element, before);
      // Moves the tail of `from` (a page, or a container being split) to the head of `to`.
      const shrink = (from: HTMLElement, to: HTMLElement, isPage: boolean) => {
        while (!fits(page!)) {
          const items = isPage ? flowOf(from) : [...itemsOf(from).children] as HTMLElement[];
          const last = items[items.length - 1];
          if (!last || items.length <= 1) {
            if (last && splittable(last)) {
              const clone = last.cloneNode(false) as HTMLElement;
              if (last.tagName === 'TABLE') clone.appendChild(doc.createElement('tbody'));
              place(clone, isPage ? to : itemsOf(to), isPage ? anchor() : itemsOf(to).firstChild as Element | null);
              shrink(last, clone, false);
            }
            return;
          }
          if (splittable(last)) {
            const clone = last.cloneNode(false) as HTMLElement;
            if (last.tagName === 'TABLE') clone.appendChild(doc.createElement('tbody'));
            place(clone, isPage ? to : itemsOf(to), isPage ? anchor() : itemsOf(to).firstChild as Element | null);
            shrink(last, clone, false);
            if (itemsOf(last).children.length === 0) last.remove();
            if (itemsOf(clone).children.length === 0) clone.remove();
            if (!fits(page!)) continue;
            return;
          }
          place(last, isPage ? to : itemsOf(to), isPage ? anchor() : itemsOf(to).firstChild as Element | null);
        }
      };
      shrink(page, next, true);
      // A heading left at the foot of a sheet goes with what it heads; a closing declaration does not
      // stand alone on a sheet - what it closes goes with it.
      const tail = flowOf(page);
      const lastLeft = tail[tail.length - 1];
      if (lastLeft && /^H[1-4]$/.test(lastLeft.tagName) && tail.length > 1) next.insertBefore(lastLeft, anchor());
      const moved = flowOf(next);
      const kept = flowOf(page);
      if (moved.length === 1 && moved[0]!.matches('.agree') && kept.length > 2) next.insertBefore(kept[kept.length - 1]!, moved[0]!);
    }
    page = page.nextElementSibling?.classList.contains('page') ? page.nextElementSibling as HTMLElement : null;
  }
  const sheets = [...doc.querySelectorAll<HTMLElement>('.page')];
  sheets.forEach((sheet, index) => sheet.querySelectorAll('.pno').forEach((slot) => { slot.textContent = `${index + 1}/${sheets.length}`; }));
}
